import clsx from "clsx";
import { useEffect, useState, type ReactNode } from "react";
import { MobileSheet } from "./MobileSheet.tsx";
import { InformationNewsDetail } from "./InformationNewsDetail.tsx";
import { getOwnerLetterDecisionCta, OwnerLetterContent, useOwnerLetter } from "./OwnerLetter.tsx";
import {
  MobileActionableEmptyState,
  mobileActionButtonClassNames,
  MobileContentHero,
  MobileDecisionQueueItem,
  MobileExpertDisclosure,
} from "../mobile/mobilePrimitives.tsx";
import i18n from "../util/i18n.ts";
import { translateMessageFrom, translateMessageSubject } from "../util/messageTranslations.ts";
import { toWorker } from "../util/toWorker.ts";
import { translateEventText } from "../util/eventText.ts";
import {
  classifyInboxMessage,
  defaultInboxFilter,
  filterInboxMessages,
  getInboxCategoryCounts,
  getInboxDecisionQueueSections,
  getInboxMessageDecisionContext,
  type InboxMessageDecisionContext,
  type InboxMessageFilter,
  type InboxMessageQueueGroup,
  type InboxMessageQueueSection,
} from "../views/messageDisplay.ts";
import {
  filterInformationMessages,
  filterInformationNews,
  getInformationNewsExcerpt,
  getInformationNewsSource,
  getInformationSourceCounts,
  type InformationNews,
  type InformationSourceFilter,
} from "./informationFeed.ts";

// =============================================================================
// M3-5: the standalone inbox page (view "inbox", route /l/:lid/inbox) and the
// message page (view "message", routes /l/:lid/message[/:mid]) — the core owner-
// feedback entry of the game loop — were retired into this ONE two-level
// MobileSheet, opened in place from the league home surface. It never touches the
// navigation back stack (see MOBILE_SHEET_CONTRACT_V1 in MobileSheet.tsx): it is
// pure component state owned by LeagueDashboard.
//
// LEVEL 1 (list): the inbox message queue exactly as the page rendered it —
//   must-decide / unread-updates / history sections, unread emphasis, and the
//   pending/updates/history filter. Fetched on open via getInboxSheet().
// LEVEL 2 (letter): tapping a row drills INSIDE the same sheet to the letter —
//   owner voice body, the owner-trust chart, and the decision CTA. The header
//   back affordance returns to the list; it is NOT a second stacked modal and
//   touches no URL. Fetched via getMessageSheet(mid), which is where the mark-as-
//   read side effect fires (server-side), reproducing the old page-view behavior.
//
// EXITS (per the sheet contract): close (onClose) or NAVIGATE (the decision CTA
// leaves for the business surface, unmounting the sheet with the home view).
//
// Landing: worker/UI deep links that used to point at /message no longer exist as
// routes. Producers now land on the home surface with a one-shot query param —
// ?m_open_mid=<mid> (a specific letter) or ?m_open_latest=1 (the latest pending
// letter, reproducing the old no-mid auto-redirect). LeagueDashboard reads the
// param once on mount, opens this sheet at the right level, and strips it via
// history.replaceState (host-page landing logic, not the sheet touching the
// stack). Old /message/:mid URLs 404 like the other deleted routes.
// =============================================================================

type InboxSheetMessage = {
  from: string;
  mid: number;
  read: boolean;
  subject: string | undefined;
  text: string;
  year: number;
};

const Spinner = ({ marker }: { marker: string }) => (
  <div className="d-flex justify-content-center py-4" {...{ [marker]: "" }}>
    <div className="spinner-border text-secondary" role="status">
      <span className="visually-hidden">
        {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
      </span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Level 1 — the message queue (ported from the deleted Inbox page).
// ---------------------------------------------------------------------------

const inboxQueueSectionMarkers = {
  history: { "data-v5-inbox-history": "" },
  mustDecide: { "data-v5-inbox-must-decide": "" },
  unreadUpdates: { "data-v5-inbox-unread-updates": "" },
} satisfies Record<InboxMessageQueueGroup, Record<string, string>>;

const getActionLabel = (action: InboxMessageDecisionContext["action"]) => {
  switch (action) {
    case "openDecision":
      return i18n.t("inbox.actionOpenDecision", { ns: "ui", defaultValue: "Open decision" });
    case "readUpdate":
      return i18n.t("inbox.actionReadUpdate", { ns: "ui", defaultValue: "Read update" });
    case "viewRecord":
      return i18n.t("inbox.actionViewRecord", { ns: "ui", defaultValue: "View record" });
  }
};

const getConsequenceLabel = (consequence: InboxMessageDecisionContext["consequence"]) => {
  switch (consequence) {
    case "decisionRisk":
      return i18n.t("inbox.consequenceDecisionRisk", {
        ns: "ui",
        defaultValue: "Put it off and you could stall the sim or miss your chance.",
      });
    case "unreadContext":
      return i18n.t("inbox.consequenceUnreadContext", {
        ns: "ui",
        defaultValue: "Read this before making the next league move.",
      });
    case "contextOnly":
      return i18n.t("inbox.consequenceContextOnly", {
        ns: "ui",
        defaultValue: "Useful context, no immediate decision.",
      });
    case "recordOnly":
      return i18n.t("inbox.consequenceRecordOnly", {
        ns: "ui",
        defaultValue: "Historical record, already resolved.",
      });
  }
};

const getDeadlineLabel = (deadline: InboxMessageDecisionContext["deadline"]) => {
  switch (deadline) {
    case "beforeContinue":
      return i18n.t("inbox.deadlineBeforeContinue", {
        ns: "ui",
        defaultValue: "Before continuing",
      });
    case "whenReady":
      return i18n.t("inbox.deadlineWhenReady", {
        ns: "ui",
        defaultValue: "Read when you want the details",
      });
    case "none":
      return undefined;
  }
};

const getKindLabel = (kind: InboxMessageDecisionContext["kind"]) => {
  switch (kind) {
    case "decision":
      return i18n.t("inbox.kindDecision", { ns: "ui", defaultValue: "Decision" });
    case "update":
      return i18n.t("inbox.kindUpdate", { ns: "ui", defaultValue: "Update" });
    case "record":
      return i18n.t("inbox.kindRecord", { ns: "ui", defaultValue: "Record" });
  }
};

const getKindIconClassName = (kind: InboxMessageDecisionContext["kind"]) => {
  switch (kind) {
    case "decision":
      return "glyphicon-exclamation-sign";
    case "update":
      return "glyphicon-info-sign";
    case "record":
      return "glyphicon-ok";
  }
};

const getCategoryLabel = (category: ReturnType<typeof classifyInboxMessage>) => {
  switch (category) {
    case "task":
      return i18n.t("inbox.categoryTask", { ns: "ui", defaultValue: "Task" });
    case "unread":
      return i18n.t("inbox.categoryUnread", { ns: "ui", defaultValue: "Unread" });
    case "completed":
      return i18n.t("inbox.categoryCompleted", { ns: "ui", defaultValue: "Completed" });
    case "info":
      return i18n.t("inbox.categoryInfo", { ns: "ui", defaultValue: "Info" });
  }
};

const getInformationSourceLabel = (source: InformationSourceFilter) => {
  switch (source) {
    case "all":
      return i18n.t("information.filterAll", { ns: "ui", defaultValue: "All" });
    case "owner":
      return i18n.t("information.filterOwner", { ns: "ui", defaultValue: "Owner" });
    case "team":
      return i18n.t("information.filterTeam", { ns: "ui", defaultValue: "Team" });
    case "league":
      return i18n.t("information.filterLeague", { ns: "ui", defaultValue: "League" });
  }
};

const getQueueSectionTitle = (group: InboxMessageQueueGroup) => {
  switch (group) {
    case "mustDecide":
      return i18n.t("inbox.sectionMustDecide", { ns: "ui", defaultValue: "Must decide" });
    case "unreadUpdates":
      return i18n.t("inbox.sectionUnreadUpdates", { ns: "ui", defaultValue: "Unread updates" });
    case "history":
      return i18n.t("inbox.sectionHistory", { ns: "ui", defaultValue: "History" });
  }
};

const getQueueSectionEmpty = (group: InboxMessageQueueGroup) => {
  switch (group) {
    case "mustDecide":
      return i18n.t("inbox.sectionMustDecideEmpty", {
        ns: "ui",
        defaultValue: "Nothing needs your call right now.",
      });
    case "unreadUpdates":
      return i18n.t("inbox.sectionUnreadUpdatesEmpty", {
        ns: "ui",
        defaultValue: "No unread updates.",
      });
    case "history":
      return i18n.t("inbox.sectionHistoryEmpty", { ns: "ui", defaultValue: "No history yet." });
  }
};

const InboxListLevel = ({
  anyUnread,
  initialFilter,
  messages,
  onOpenMessage,
  onOpenNews,
  news,
  summaryHeader,
  userTid,
}: {
  anyUnread: boolean;
  initialFilter?: InboxMessageFilter;
  messages: InboxSheetMessage[];
  onOpenMessage: (mid: number) => void;
  onOpenNews: (eid: number) => void;
  news?: InformationNews[];
  summaryHeader?: ReactNode;
  userTid?: number;
}) => {
  const [filter, setFilter] = useState<InboxMessageFilter>(
    () => initialFilter ?? defaultInboxFilter(messages),
  );
  const [sourceFilter, setSourceFilter] = useState<InformationSourceFilter>("all");
  const unifiedInformation = news !== undefined;
  const newsItems = news ?? [];
  const categoryCounts = getInboxCategoryCounts(messages);
  const activeFilter = unifiedInformation
    ? "all"
    : filter === "tasks" && categoryCounts.task === 0
      ? defaultInboxFilter(messages)
      : filter;
  const statusFilteredMessages = filterInboxMessages(messages, activeFilter);
  const visibleMessages = filterInformationMessages({
    messages: statusFilteredMessages,
    source: sourceFilter,
  });
  const visibleNews = filterInformationNews({
    news: newsItems,
    source: sourceFilter,
    userTid,
  });
  const queueSections = getInboxDecisionQueueSections(visibleMessages);
  const sourceCounts = getInformationSourceCounts({
    messages,
    news: newsItems,
    userTid,
  });
  const unreadCount = messages.filter((message) => !message.read).length;

  const filterItems = [
    {
      count: categoryCounts.task,
      key: "tasks" as const,
      label: i18n.t("inbox.filterTasks", { ns: "ui", defaultValue: "Pending" }),
    },
    {
      count: categoryCounts.task + categoryCounts.unread,
      key: "unread" as const,
      label: i18n.t("inbox.filterUnread", { ns: "ui", defaultValue: "Updates" }),
    },
    {
      count: messages.length,
      key: "all" as const,
      label: i18n.t("inbox.filterHistory", { ns: "ui", defaultValue: "History" }),
    },
  ];
  const visibleFilterItems = filterItems.filter(
    (item) => item.key !== "tasks" || categoryCounts.task > 0,
  );
  const showFilterControls =
    !unifiedInformation && (categoryCounts.task > 0 || categoryCounts.unread > 0);
  const sourceFilterItems = (["all", "owner", "team", "league"] as const).map((source) => ({
    count: sourceCounts[source],
    label: getInformationSourceLabel(source),
    source,
  }));

  const renderQueueSectionBody = (section: InboxMessageQueueSection) => (
    <>
      {section.key === "history" ? null : (
        <h2 className="h6 mb-2">{getQueueSectionTitle(section.key)}</h2>
      )}
      {section.messages.length === 0 ? (
        <p className="small text-body-secondary mb-0">{getQueueSectionEmpty(section.key)}</p>
      ) : (
        <div className="d-grid gap-2">
          {section.messages.map(({ from, mid, read, subject, text, year }) => {
            const message = { from, mid, read, subject, text, year };
            const title =
              translateMessageSubject(subject ?? "") ??
              i18n.t("inbox.messageFallbackSubject", { ns: "ui", defaultValue: "Message" });
            const translatedFrom = translateMessageFrom(from ?? "");
            const category = classifyInboxMessage(message);
            const decisionContext = getInboxMessageDecisionContext(message);
            const deadlineLabel = getDeadlineLabel(decisionContext.deadline);

            return (
              <MobileDecisionQueueItem
                data-information-center-message={mid}
                action={
                  <button
                    className={clsx("btn btn-sm", {
                      "btn-outline-primary": decisionContext.kind !== "decision",
                      "btn-primary": decisionContext.kind === "decision",
                    })}
                    data-inbox-sheet-open-message=""
                    onClick={() => onOpenMessage(mid)}
                    type="button"
                  >
                    {getActionLabel(decisionContext.action)}
                  </button>
                }
                className={clsx({ "border border-primary": !read })}
                data-mobile-inbox-message-category={category}
                data-v5-inbox-message=""
                deadline={deadlineLabel}
                consequence={getConsequenceLabel(decisionContext.consequence)}
                key={mid}
                label={
                  <span className="mobile-inbox-message-status">
                    <span
                      aria-hidden="true"
                      className={clsx(
                        "glyphicon",
                        getKindIconClassName(decisionContext.kind),
                        "mobile-inbox-message-status-icon",
                      )}
                    />{" "}
                    {getKindLabel(decisionContext.kind)} · {getCategoryLabel(category)} ·{" "}
                    {i18n.t("inbox.messageMeta", {
                      ns: "ui",
                      defaultValue: "{{from}}, {{year}}",
                      from: translatedFrom,
                      year,
                    })}
                  </span>
                }
                title={<span className={clsx({ "fw-bold": !read })}>{title}</span>}
                urgency={decisionContext.urgency}
              />
            );
          })}
        </div>
      )}
    </>
  );

  const renderQueueSection = (section: InboxMessageQueueSection) => {
    if (section.key === "history") {
      return (
        <MobileExpertDisclosure
          className="mobile-inbox-history-depth"
          expertHostId="inbox-history"
          key={section.key}
          open={false}
          summary={getQueueSectionTitle(section.key)}
          {...inboxQueueSectionMarkers[section.key]}
        >
          {renderQueueSectionBody(section)}
        </MobileExpertDisclosure>
      );
    }

    return (
      <section
        className="mobile-inbox-queue-section"
        key={section.key}
        {...inboxQueueSectionMarkers[section.key]}
      >
        {renderQueueSectionBody(section)}
      </section>
    );
  };

  const renderNewsList = () =>
    visibleNews.length > 0 ? (
      <section data-information-center-news-list="">
        <h2 className="h6 mb-2">
          {i18n.t("information.newsSection", {
            ns: "ui",
            defaultValue: "League and team updates",
          })}
        </h2>
        <div className="d-grid gap-2">
          {visibleNews.map((item) => {
            const source = getInformationNewsSource(item, userTid);
            const title = i18n.t("information.newsFallbackTitle", {
              ns: "ui",
              defaultValue: "News update",
            });

            return (
              <MobileDecisionQueueItem
                action={
                  <button
                    className="btn btn-outline-primary btn-sm"
                    data-information-center-open-news={item.eid}
                    onClick={() => onOpenNews(item.eid)}
                    type="button"
                  >
                    {i18n.t("information.newsAction", {
                      ns: "ui",
                      defaultValue: "Read update",
                    })}
                  </button>
                }
                consequence={getInformationNewsExcerpt(translateEventText(item))}
                data-information-center-news={item.eid}
                data-information-center-news-source={source}
                key={item.eid}
                label={i18n.t("information.newsMeta", {
                  ns: "ui",
                  defaultValue: "{{source}} · {{season}}",
                  season: item.season,
                  source: getInformationSourceLabel(source),
                })}
                title={title}
                urgency="none"
              />
            );
          })}
        </div>
      </section>
    ) : null;

  return (
    <>
      {unifiedInformation && unreadCount > 0 ? (
        <div className="small text-body-secondary mb-3" data-information-center-unread-summary="">
          {i18n.t("information.unreadSummary", {
            count: unreadCount,
            ns: "ui",
            defaultValue: "{{count}} unread letters",
          })}
        </div>
      ) : !unifiedInformation && anyUnread ? (
        <div className="alert alert-danger" role="alert">
          {i18n.t("inbox.newMessage", {
            ns: "ui",
            defaultValue: "You have a new message. Read it before continuing.",
          })}
        </div>
      ) : null}

      <MobileContentHero data-v5-inbox-content-first-screen="" data-v5-inbox-queue-first-screen="">
        {summaryHeader !== undefined ? (
          <div className="mb-3" data-information-center-summary-header="">
            {summaryHeader}
          </div>
        ) : null}
        {sourceCounts.all === 0 ? (
          // G4-1: the inbox is a "tool" sheet — its only exit is the header X.
          // The old "返回首页并继续" button duplicated that X (UX review 意见1),
          // so the empty state is now just the title + conclusion statement.
          <MobileActionableEmptyState
            data-v5-inbox-empty-state=""
            impact={i18n.t("information.emptyImpact", {
              ns: "ui",
              defaultValue: "New messages will appear here.",
            })}
            reason="empty"
            reasonText={i18n.t("information.emptyReason", {
              ns: "ui",
              defaultValue: "No messages are available.",
            })}
            title={i18n.t("information.emptyTitle", {
              ns: "ui",
              defaultValue: "No messages",
            })}
          />
        ) : (
          <>
            {unifiedInformation ? (
              <div
                className="d-flex flex-wrap gap-2 mb-3"
                data-information-center-filters=""
                role="group"
              >
                {sourceFilterItems.map((item) => (
                  <button
                    className={clsx("btn btn-sm", {
                      "btn-primary": sourceFilter === item.source,
                      "btn-outline-primary": sourceFilter !== item.source,
                    })}
                    data-information-center-filter={item.source}
                    key={item.source}
                    onClick={() => setSourceFilter(item.source)}
                    type="button"
                  >
                    {item.label} <span className="badge text-bg-light">{item.count}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {showFilterControls ? (
              <div
                className="btn-group mb-3"
                data-mobile-inbox-filters=""
                data-v5-inbox-filter-entry=""
                role="group"
              >
                {visibleFilterItems.map((item) => (
                  <button
                    className={clsx("btn btn-sm", {
                      "btn-primary": activeFilter === item.key && item.key !== "all",
                      "btn-outline-primary": activeFilter !== item.key && item.key !== "all",
                      "btn-outline-secondary": item.key === "all",
                    })}
                    key={item.key}
                    onClick={() => setFilter(item.key)}
                    type="button"
                  >
                    {item.label} <span className="badge text-bg-light">{item.count}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {visibleMessages.length === 0 && visibleNews.length === 0 ? (
              <MobileActionableEmptyState
                action={
                  <button
                    className={mobileActionButtonClassNames.secondary}
                    data-v5-inbox-filtered-empty-action=""
                    onClick={() => {
                      setFilter(defaultInboxFilter(messages));
                      setSourceFilter("all");
                    }}
                    type="button"
                  >
                    {i18n.t("information.showAll", {
                      ns: "ui",
                      defaultValue: "Show all information",
                    })}
                  </button>
                }
                data-v5-inbox-filtered-empty-state=""
                impact={i18n.t("information.filteredEmptyImpact", {
                  ns: "ui",
                  defaultValue: "Switch back to all sources to see everything.",
                })}
                reason="filtered"
                reasonText={i18n.t("information.filteredEmptyReason", {
                  ns: "ui",
                  defaultValue: "No information matches this source.",
                })}
                title={i18n.t("information.filteredEmptyTitle", {
                  ns: "ui",
                  defaultValue: "Nothing here",
                })}
              />
            ) : (
              <div
                className="d-grid gap-3"
                data-information-center-list=""
                data-v5-inbox-queue=""
                id="messages-list"
              >
                {visibleMessages.length > 0
                  ? queueSections.map((section) => renderQueueSection(section))
                  : null}
                {renderNewsList()}
              </div>
            )}
          </>
        )}
      </MobileContentHero>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sheet shell — owns the two levels + the on-open fetch pattern. The level-2
// letter itself is the shared OwnerLetter piece (also used by OwnerLetterDialog).
// ---------------------------------------------------------------------------

export const InboxSheet = ({
  initialFilter,
  initialMid,
  news,
  onClose,
  onMessageRead,
  open,
  openLatest = false,
  summaryHeader,
  userTid,
}: {
  initialFilter?: InboxMessageFilter;
  // When set, the sheet opens straight on that letter (deep-link ?m_open_mid).
  initialMid?: number;
  news?: InformationNews[];
  onClose: () => void;
  onMessageRead?: (mid: number) => void;
  open: boolean;
  // When true, drill to the latest pending letter once the list loads
  // (reproduces the old no-mid /message auto-redirect; deep-link ?m_open_latest).
  openLatest?: boolean;
  summaryHeader?: ReactNode;
  userTid?: number;
}) => {
  const [list, setList] = useState<
    { anyUnread: boolean; messages: InboxSheetMessage[] } | undefined
  >(undefined);
  const [activeMid, setActiveMid] = useState<number | undefined>(undefined);
  const [activeNewsEid, setActiveNewsEid] = useState<number | undefined>(undefined);
  // Guards the one-shot auto-drill (initialMid / openLatest) so returning to the
  // list level does not re-trigger it.
  const [autoDrillDone, setAutoDrillDone] = useState(false);

  // Level-2 letter: the shared hook fetches getMessageSheet and fires the
  // server-side mark-as-read side effect (same code path as OwnerLetterDialog).
  const letter = useOwnerLetter(activeMid, open);

  // On open: (re)fetch the list and seed the initial level from the deep-link
  // intent. Reset everything when the sheet closes so the next open is clean.
  useEffect(() => {
    if (!open) {
      setList(undefined);
      setActiveMid(undefined);
      setActiveNewsEid(undefined);
      setAutoDrillDone(false);
      return;
    }

    setActiveMid(initialMid);
    setActiveNewsEid(undefined);
    setAutoDrillDone(initialMid !== undefined);

    let cancelled = false;
    setList(undefined);
    (async () => {
      const result = await toWorker("main", "getInboxSheet", undefined);
      if (!cancelled) {
        setList({ anyUnread: result.anyUnread, messages: result.messages });
      }
    })();

    return () => {
      cancelled = true;
    };
    // initialMid/openLatest only matter at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Deferred auto-drill: openLatest needs the loaded list to pick the latest
  // unread letter (fallback: the newest letter), mirroring the old no-mid entry.
  useEffect(() => {
    if (!open || autoDrillDone || !openLatest || list === undefined) {
      return;
    }

    setAutoDrillDone(true);
    const target = list.messages.find((message) => !message.read) ?? list.messages[0];
    if (target) {
      setActiveMid(target.mid);
    }
  }, [autoDrillDone, list, open, openLatest]);

  // Optimistically reflect the now-read state in the list so the row loses its
  // unread emphasis when we return to level 1 (the shared hook owns the fetch +
  // server-side mark-as-read; this is only the list's local echo of it).
  useEffect(() => {
    if (!open || activeMid === undefined || list === undefined) {
      return;
    }

    const activeMessage = list.messages.find((message) => message.mid === activeMid);
    if (!activeMessage || activeMessage.read) {
      return;
    }

    onMessageRead?.(activeMid);

    setList((previous) =>
      previous
        ? {
            anyUnread: previous.messages.some(
              (message) => message.mid !== activeMid && !message.read,
            ),
            messages: previous.messages.map((message) =>
              message.mid === activeMid ? { ...message, read: true } : message,
            ),
          }
        : previous,
    );
  }, [activeMid, list, onMessageRead, open]);

  const activeNews = news?.find((item) => item.eid === activeNewsEid);
  const atLetterLevel = activeMid !== undefined;
  const atNewsLevel = activeNewsEid !== undefined;
  const atDetailLevel = atLetterLevel || atNewsLevel;

  const listTitle = i18n.t("information.title", { ns: "ui", defaultValue: "Information" });
  const letterTitle =
    letter && letter.subject
      ? (translateMessageSubject(letter.subject) ?? letter.subject)
      : i18n.t("message.notFoundTitle", { ns: "ui", defaultValue: "Message not found" });
  const newsTitle = i18n.t("information.newsFallbackTitle", {
    ns: "ui",
    defaultValue: "News update",
  });

  // The back affordance lives INSIDE the sheet header title node — a component-
  // state level change, never a second stacked modal or a back-stack push.
  const title: ReactNode = atDetailLevel ? (
    <span className="d-flex align-items-center gap-2">
      <button
        aria-label={i18n.t("information.backToList", {
          ns: "ui",
          defaultValue: "Back to information",
        })}
        className="btn btn-link btn-sm p-0 mobile-sheet-back-button"
        data-information-center-back=""
        data-inbox-sheet-back=""
        onClick={() => {
          setActiveMid(undefined);
          setActiveNewsEid(undefined);
        }}
        type="button"
      >
        <span aria-hidden="true" className="glyphicon glyphicon-chevron-left" />
      </button>
      <span className="text-truncate">{atLetterLevel ? letterTitle : newsTitle}</span>
    </span>
  ) : (
    listTitle
  );

  // Decision CTA — shared with OwnerLetterDialog: shown only when the letter is a
  // pending decision, a NAVIGATE exit to the closest business surface.
  let footer: ReactNode;
  if (atLetterLevel && letter) {
    const cta = getOwnerLetterDecisionCta(letter);
    if (cta) {
      footer = (
        <a className="btn btn-primary w-100" data-inbox-sheet-decision-cta="" href={cta.href}>
          {i18n.t(cta.labelKey, {
            ns: "ui",
            defaultValue: cta.defaultLabel,
          })}
        </a>
      );
    }
  }

  return (
    <MobileSheet footer={footer} onClose={onClose} open={open} title={title}>
      <div
        data-information-center-detail={atLetterLevel ? "owner" : atNewsLevel ? "news" : undefined}
        data-information-center-list={atDetailLevel ? undefined : ""}
        data-information-center-sheet=""
      >
        {atLetterLevel ? (
          letter === undefined ? (
            <Spinner marker="data-inbox-sheet-loading" />
          ) : (
            <div data-information-center-message-detail={activeMid}>
              <OwnerLetterContent message={letter ?? undefined} />
            </div>
          )
        ) : atNewsLevel ? (
          activeNews ? (
            <div data-information-center-news-detail={activeNews.eid}>
              <InformationNewsDetail
                news={activeNews}
                sourceLabel={getInformationSourceLabel(
                  getInformationNewsSource(activeNews, userTid),
                )}
                title={newsTitle}
              />
            </div>
          ) : (
            <MobileActionableEmptyState
              impact={i18n.t("information.newsMissingImpact", {
                ns: "ui",
                defaultValue: "Return to the list to choose another update.",
              })}
              reason="unavailable"
              reasonText={i18n.t("information.newsMissingReason", {
                ns: "ui",
                defaultValue: "This news item is no longer available.",
              })}
              title={i18n.t("information.newsMissingTitle", {
                ns: "ui",
                defaultValue: "News unavailable",
              })}
            />
          )
        ) : list === undefined ? (
          <Spinner marker="data-inbox-sheet-loading" />
        ) : (
          <InboxListLevel
            anyUnread={list.anyUnread}
            initialFilter={initialFilter}
            messages={list.messages}
            news={news}
            onOpenMessage={(mid) => {
              setActiveNewsEid(undefined);
              setActiveMid(mid);
            }}
            onOpenNews={(eid) => {
              setActiveMid(undefined);
              setActiveNewsEid(eid);
            }}
            summaryHeader={summaryHeader}
            userTid={userTid}
          />
        )}
      </div>
    </MobileSheet>
  );
};

export default InboxSheet;
